import { lazy, Suspense, useCallback, useState } from "react";
import { Check, Copy, Loader2 } from "lucide-react";
import type { MonacoEditorTheme } from "@ncoder/code-editor";
import type { CodePayload } from "@/stores/weaveWorkspaceStore";

/**
 * A code or shell block from a response, in the workspace.
 *
 * REUSES `@ncoder/code-editor`, which is the repo's shared Monaco surface (24 import sites) and
 * already what `FileDiff` mounts for changeset diffs. No second editor abstraction: §14 rules it
 * out, and there would be nothing to gain — this needs a read-only editor with a language and a
 * theme, which is exactly what `CodeEditor` is.
 *
 * LAZY, for the reason `FileDiff` is lazy: a static import would put `@monaco-editor/react` in the
 * graph of every module that reaches the workspace, so a run that never opens a code block would
 * still pay for the editor. The workspace is closed by default, so on most runs this never loads.
 *
 * READ-ONLY BY DEFAULT, and the exception is the point. The usual content here is a block the
 * ASSISTANT wrote inside a message, and there is no contract path to write that back: `PUT /plan`
 * edits a plan and `POST /save` writes changeset files, neither of which accepts a snippet from a
 * response. An editable buffer with nowhere to save would be the fake capability §11 forbids, so
 * Copy is the real action and the changeset board is where file edits happen.
 *
 * A payload carrying an `attachmentId` is the one case that DOES have somewhere to save: it is the
 * reader's own paste, still sitting unsent in the composer, and "saving" means updating that
 * attachment. That is not a loosening of the rule above — it is the same rule answering a different
 * question, because this time the destination exists. See `CodePayload.attachmentId`.
 */
const CodeEditor = lazy(() =>
  import("@ncoder/code-editor").then((m) => ({ default: m.CodeEditor })),
);

/** Monaco theme from the app's dark class, with a browser-free fallback. Same rule as `FileDiff`. */
function resolveEditorTheme(): MonacoEditorTheme {
  if (typeof document === "undefined") return "vs-dark";
  return document.documentElement.classList.contains("dark") ? "apex-dark" : "apex-light";
}

export interface CodeSectionProps {
  payload: CodePayload | null;
  /**
   * Write an edit back to the composer attachment this code came from.
   *
   * Supplied ONLY by a host that owns the attachment state (`MultiAgentRoute`); omitted, or paired
   * with a payload carrying no `attachmentId`, the editor stays read-only exactly as before. The
   * two conditions are deliberately both required: a host that can save is useless without a
   * destination, and a destination is useless without a host that can write to it.
   */
  onEditAttachment?: (attachmentId: string, content: string) => void;
}

export function CodeSection({ payload, onEditAttachment }: CodeSectionProps) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(payload.content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // A denied clipboard permission is not worth an error banner over — the code is on screen and
      // selectable. Silently leaving the button unchanged is the honest outcome.
    }
  }, [payload]);

  if (!payload) {
    return <p className="px-1 py-6 text-center text-[13px] text-muted-foreground">Nothing to show.</p>;
  }

  const lines = payload.content ? payload.content.split("\n").length : 0;
  /*
   * EDITABLE ONLY FOR THE READER'S OWN UNSENT PASTE — see `CodePayload.attachmentId`. Everything
   * else that reaches this panel is a block the assistant wrote, which has nowhere to save to.
   */
  const attachmentId = payload.attachmentId;
  const editable = !!attachmentId && !!onEditAttachment;

  return (
    <section aria-label={payload.title ?? `${payload.language} block`} className="flex min-h-0 flex-col gap-2">
      <header className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          {/*
            NO NAME HERE. The workspace header above already reads the block's own name — it takes
            its title from this same payload — and printing it again put
            "OpportunityApprovalService.cls" twice, one line apart. What this line adds is what the
            header cannot say: the language, the size, and why there is no Save.
          */}
          <p className="text-[12px] text-muted-foreground">
            {payload.language ? `${payload.language} · ` : ""}
            {lines} line{lines === 1 ? "" : "s"}
            {/*
              Says what the reader can DO here, rather than leaving them to find out by trying.
              "read-only" is the honest word for an assistant block with no save path; an unsent
              paste says where its edits go instead, because "edits apply on send" is the one thing
              that is not obvious about editing something that has not been sent yet.
            */}
            {editable ? " · edits apply when you send" : " · read-only"}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void copy()}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-[12px] font-medium text-foreground hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {copied ? <Check aria-hidden className="h-3.5 w-3.5" /> : <Copy aria-hidden className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </header>

      {/*
        A fixed height rather than `flex-1`: Monaco measures its container, and a zero-height parent
        during the first layout pass makes it initialise at 5px and never recover. `min(60vh, …)`
        keeps it inside the sheet at narrow widths.
      */}
      <div className="overflow-hidden rounded-md border border-border" style={{ height: "min(60vh, 560px)" }}>
        <Suspense
          fallback={
            <p role="status" className="flex h-full items-center justify-center gap-2 text-[13px] text-muted-foreground">
              <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
              Opening the editor…
            </p>
          }
        >
          <CodeEditor
            value={payload.content}
            language={payload.monaco}
            readOnly={!editable}
            /*
             * Uncontrolled from React's side on purpose: Monaco owns the buffer while the reader
             * types, and feeding every keystroke back through `value` would fight the cursor. The
             * attachment is updated as the source of truth for SEND, not as the editor's state.
             */
            onChange={editable ? (next) => onEditAttachment!(attachmentId!, next) : undefined}
            theme={resolveEditorTheme()}
            fileName={payload.title ?? undefined}
          />
        </Suspense>
      </div>
    </section>
  );
}
